// src/components/CategoriesModal.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  createExpenseCategory,
  deleteExpenseCategory,
  listExpenseCategories,
  type ExpenseCategory,
} from "../../services/financeApi";

const Classes = {
  overlay: "fixed inset-0 bg-black/40 z-40",
  card:
    "fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 " +
    "w-[92vw] max-w-xl bg-white rounded-md border shadow-lg",
  header: "px-4 py-3 border-b flex items-center justify-between",
  body: "p-4 space-y-3",
  footer: "px-4 py-3 border-t flex items-center justify-end gap-2",
  input:
    "border border-slate-300 rounded-md px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-600/30",
  button:
    "px-3 py-1.5 rounded-md border text-sm transition disabled:opacity-50",
  primary: "bg-blue-600 text-white border-blue-600 hover:bg-blue-700",
  ghost: "bg-white border-slate-300 hover:bg-slate-50",
  danger: "text-red-600 hover:bg-red-50 border-red-200",
  badge:
    "inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-slate-100 text-slate-700",
  row: "flex items-center justify-between gap-2 border rounded-md px-3 py-2",
};

export default function CategoriesModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<ExpenseCategory[]>([]);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const fetchRows = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listExpenseCategories();
      setRows(data);
    } catch (e: any) {
      setError(e?.message || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setQ("");
      setNewName("");
      setCreating(false);
      fetchRows();
    }
  }, [open]);

  const filtered = useMemo(() => {
    const qq = q.trim().toLowerCase();
    if (!qq) return rows;
    return rows.filter((r) => r.name.toLowerCase().includes(qq));
  }, [rows, q]);

  const createRow = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const created = await createExpenseCategory(newName.trim());
      setRows((prev) =>
        [created, ...prev].sort((a, b) => a.name.localeCompare(b.name))
      );
      setNewName("");
    } catch (e: any) {
      const msg =
        e?.response?.data?.message || e?.message || "Failed to create";
      setError(msg);
    } finally {
      setCreating(false);
    }
  };

  const deleteRow = async (id: string) => {
    const ok = window.confirm("Delete this category?");
    if (!ok) return;
    try {
      await deleteExpenseCategory(id);
      setRows((prev) => prev.filter((r) => r._id !== id));
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || "Failed to delete");
    }
  };

  if (!open) return null;

  return (
    <>
      <div className={Classes.overlay} onClick={onClose} />
      <div className={Classes.card} role="dialog" aria-modal="true">
        <div className={Classes.header}>
          <div>
            <div className="text-sm font-semibold">
              Manage Expense Categories
            </div>
            <div className="text-[11px] text-slate-500">
              Used by Business Expenses & analysis
            </div>
          </div>
          <button
            onClick={onClose}
            className={`${Classes.button} ${Classes.ghost}`}
          >
            Close
          </button>
        </div>

        <div className={Classes.body}>
          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
              {error}
            </div>
          )}

          {/* Create */}
          <div className="space-y-2">
            <label className="text-xs text-slate-600">Add category</label>
            <div className="flex items-center gap-2">
              <input
                className={Classes.input}
                placeholder="e.g., Booth Fees"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createRow()}
              />
              <button
                onClick={createRow}
                disabled={!newName.trim() || creating}
                className={`${Classes.button} ${Classes.primary}`}
              >
                {creating ? "Adding..." : "Add"}
              </button>
            </div>
          </div>

          {/* Search */}
          <div className="space-y-2">
            <label className="text-xs text-slate-600">Search</label>
            <input
              className={Classes.input}
              placeholder="Type to filter…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </div>

          {/* List */}
          <div className="space-y-2 max-h-72 overflow-auto border rounded-md p-2 bg-slate-50/30">
            {loading ? (
              <div className="text-sm text-slate-600 px-2 py-3">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="text-sm text-slate-600 px-2 py-3">
                No categories.
              </div>
            ) : (
              filtered.map((r) => (
                <div key={r._id} className={Classes.row}>
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={Classes.badge}>#{r._id.slice(-5)}</span>
                    <div className="truncate">{r.name}</div>
                  </div>
                  <button
                    onClick={() => deleteRow(r._id)}
                    className={`${Classes.button} ${Classes.danger}`}
                    title="Delete"
                  >
                    Delete
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <div className={Classes.footer}>
          <button
            onClick={onClose}
            className={`${Classes.button} ${Classes.ghost}`}
          >
            Done
          </button>
        </div>
      </div>
    </>
  );
}
